import React, {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";

type NewWindowProps = {
  children: React.ReactNode;
  title?: string;
  onClose?: () => void;
};

export function NewWindow({ children, onClose, title = "New Window" }: NewWindowProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const newWindow = useRef<Window | null>(null);

  useEffect(() => {
    // Open a new browser window
    newWindow.current = window.open(
      "",
      "",
      "width=600,height=400,left=200,top=200"
    );

    if (!newWindow.current) return;

    // Set title
    newWindow.current.document.title = title;

    // Create a container div
    const div = document.createElement("div");
    newWindow.current.document.body.appendChild(div);
    setContainer(div);

    injectAllStylingIntoNewWindow(newWindow);

    // Handle window close
    const handleBeforeUnload = () => {
      if (onClose) {
        onClose();
      }
    }

    newWindow.current.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup when component unmounts
    const curWindow = newWindow.current;
    return () => {
      curWindow.close();
    };
  }, [title, onClose]);

  // Render children into the new window
  return container ? ReactDOM.createPortal(children, container) : null;
}

function injectAllStylingIntoNewWindow(newWindow: React.MutableRefObject<Window | null>): void {
  // Inject all the style sheets in the current window into the new window
  Array.from(document.styleSheets).forEach((styleSheet) => {
    try {
      if (styleSheet.cssRules) {
        // Create a new style element
        const newStyleEl = document.createElement("style");
        Array.from(styleSheet.cssRules).forEach((cssRule) => {
          newStyleEl.appendChild(document.createTextNode(cssRule.cssText));
        });
        newWindow.current?.document.head.appendChild(newStyleEl);
      }
    } catch (e) {
      // If the stylesheet is from another domain, it may throw a security error
      console.warn("Could not access stylesheet:", styleSheet.href, e);
    }
  });
}

